import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Cpu, RefreshCcw, Save, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { AIFeatureKey } from '../../../utils/sumopod';
import { useNavigate } from 'react-router-dom';

interface AIModelSetting {
    fitur_key: AIFeatureKey;
    nama_fitur: string;
    deskripsi: string;
    model_utama: string;
    model_fallback: string;
    updated_at: string;
}

const PortalGuruPengaturanAI: React.FC = () => {
    const navigate = useNavigate();
    const [settings, setSettings] = useState<AIModelSetting[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('pengaturan_ai_model')
                .select('*')
                .order('nama_fitur', { ascending: true });

            if (error) throw error;

            let currentSettings = data || [];

            // Auto-seed required features if missing
            const requiredFeatures = [
                { fitur_key: 'scan_nilai_kognitif', nama_fitur: 'Scan LJK (Nilai Kognitif)', deskripsi: 'Membaca LJK manual untuk form kognitif', model_utama: 'gpt-5-vision', model_fallback: 'gpt-5.4-vision-mini' },
                { fitur_key: 'scan_nilai_kepribadian', nama_fitur: 'Scan LJK (Nilai Kepribadian)', deskripsi: 'Membaca LJK manual untuk form kepribadian', model_utama: 'gpt-5-vision', model_fallback: 'gpt-5.4-vision-mini' },
                { fitur_key: 'scan_mutabaah', nama_fitur: 'Scan LJK (Mutabaah)', deskripsi: 'Membaca LJK manual untuk form mutabaah harian', model_utama: 'gpt-5-vision', model_fallback: 'gpt-5.4-vision-mini' },
                { fitur_key: 'koreksi_ljk', nama_fitur: 'Koreksi Auto-Grading (LJK)', deskripsi: 'Mengoreksi kunci jawaban LJK', model_utama: 'gpt-5.4-mini', model_fallback: 'gpt-5-mini' },
                { fitur_key: 'catatan_rapor', nama_fitur: 'Catatan Rapor & Mutabaah', deskripsi: 'Generator text AI untuk catatan rapor', model_utama: 'deepseek-v4-pro', model_fallback: 'deepseek-v4-lite' },
                { fitur_key: 'parsing_alamat', nama_fitur: 'Parsing Alamat/Lokasi', deskripsi: 'Mengubah raw text alamat menjadi struktur Maps', model_utama: 'gpt-5.4-mini', model_fallback: 'gpt-5-mini' },
                { fitur_key: 'analisis_kepribadian', nama_fitur: 'Analisis Kepribadian Peringkat', deskripsi: 'Menganalisis tren nilai historis karakter pada halaman statistik peringkat', model_utama: 'gpt-4o', model_fallback: 'gpt-4o-mini' }
            ];

            const missingFeatures = requiredFeatures.filter(rf => !currentSettings.some(cs => cs.fitur_key === rf.fitur_key));

            if (missingFeatures.length > 0) {
                 // Even if it fails to insert (due to RLS or missing schema), show them in UI
                 currentSettings = [...currentSettings, ...missingFeatures].sort((a, b) => a.nama_fitur.localeCompare(b.nama_fitur));

                 // Attempt background insert silently
                 supabase.from('pengaturan_ai_model').insert(missingFeatures).then();
            }

            setSettings(currentSettings);
        } catch (error: any) {
            setMessage({ type: 'error', text: `Gagal memuat pengaturan: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const handleInputChange = (index: number, field: 'model_utama' | 'model_fallback', value: string) => {
        const newSettings = [...settings];
        newSettings[index][field] = value;
        setSettings(newSettings);
    };

    const handleSave = async (index: number) => {
        setSaving(true);
        setMessage(null);
        try {
            const setting = settings[index];
            const { error } = await supabase
                .from('pengaturan_ai_model')
                .update({
                    model_utama: setting.model_utama,
                    model_fallback: setting.model_fallback,
                    updated_at: new Date().toISOString()
                })
                .eq('fitur_key', setting.fitur_key);

            if (error) throw error;

            setMessage({ type: 'success', text: `Berhasil menyimpan pengaturan untuk ${setting.nama_fitur}` });
            setTimeout(() => setMessage(null), 3000);
        } catch (error: any) {
             setMessage({ type: 'error', text: `Gagal menyimpan: ${error.message}` });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-20 font-sans">
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-30 shadow-sm">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-lg font-bold text-gray-900 leading-tight">Pengaturan Model AI</h1>
                            <p className="text-xs text-gray-500 font-medium">SumoPod Konfigurasi</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-4 max-w-4xl mx-auto space-y-6 mt-4">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                        <div>
                            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                                <Cpu className="w-6 h-6 text-indigo-600" />
                                Pengaturan Model AI (SumoPod)
                            </h2>
                            <p className="text-slate-500 text-sm mt-1">Konfigurasi jenis model AI yang digunakan untuk setiap fitur secara mandiri. Jangan lupa untuk simpan perubahan.</p>
                        </div>
                        <button
                            onClick={fetchSettings}
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Refresh Data"
                        >
                            <RefreshCcw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>

            {message && (
                <div className={`m-6 p-4 rounded-xl flex items-center gap-3 ${
                    message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-red-50 text-red-700 border border-red-100'
                }`}>
                    {message.type === 'success' ? <CheckCircle2 className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                    <p className="text-sm font-medium">{message.text}</p>
                </div>
            )}

            <div className="p-6">
                {loading ? (
                    <div className="space-y-4 animate-pulse">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-32 bg-slate-100 rounded-xl"></div>
                        ))}
                    </div>
                ) : settings.length === 0 ? (
                    <div className="text-center py-10 text-slate-500">
                        Belum ada konfigurasi AI di database.
                    </div>
                ) : (
                    <div className="grid gap-6">
                        {settings.map((setting, index) => (
                            <div key={setting.fitur_key} className="p-5 border border-slate-200 rounded-xl hover:border-indigo-200 transition-colors bg-white shadow-sm">
                                <div className="mb-4">
                                    <h3 className="font-semibold text-slate-800">{setting.nama_fitur}</h3>
                                    <p className="text-sm text-slate-500 mt-1">{setting.deskripsi}</p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-slate-700 mb-1">Model Utama</label>
                                        <input
                                            type="text"
                                            value={setting.model_utama}
                                            onChange={(e) => handleInputChange(index, 'model_utama', e.target.value)}
                                            className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                                            placeholder="e.g. gpt-5-vision"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-slate-700 mb-1">Model Fallback</label>
                                        <input
                                            type="text"
                                            value={setting.model_fallback || ''}
                                            onChange={(e) => handleInputChange(index, 'model_fallback', e.target.value)}
                                            className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                                            placeholder="e.g. gpt-5.4-vision-mini"
                                        />
                                    </div>
                                </div>
                                <div className="mt-4 flex justify-end">
                                    <button
                                        onClick={() => handleSave(index)}
                                        disabled={saving}
                                        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                                    >
                                        <Save className="w-4 h-4" />
                                        Simpan
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                </div>
            </div>
        </div>
        </div>
    );
};

export default PortalGuruPengaturanAI;
